/**
 * Wires push notifications to the auth session and the router.
 *
 * - Registers the device token once per signed-in user (iOS system permission
 *   prompt appears right after login, per product decision).
 * - Routes notification taps: the push payload carries the announcement's
 *   `link`, which goes through resolveAppLink() exactly like a bell-dropdown
 *   click — in-app links push onto the router, everything else opens
 *   externally.
 *
 * All expo-notifications access goes through lib/pushNotifications, which is
 * a no-op stub on web.
 */
import { useAuth } from '@/lib/AuthContext';
import { logger } from '@/lib/logger';
import {
  addNotificationTapListener,
  getInitialNotificationTap,
  registerForPushNotifications,
  type NotificationTap,
} from '@/lib/pushNotifications';
import { resolveAppLink } from '@/lib/resolveAppLink';
import { router } from 'expo-router';
import { useEffect, useRef } from 'react';
import { Linking, Platform } from 'react-native';

export function usePushNotifications(): void {
  const { user } = useAuth();
  const registeredUserIdRef = useRef<string | null>(null);
  const handledTapIdsRef = useRef<Set<string>>(new Set());

  const userId = user && !user.is_anonymous ? user.id : null;

  // Register the device token after login (and on cold start with a session).
  useEffect(() => {
    if (Platform.OS === 'web' || !userId) return;
    if (registeredUserIdRef.current === userId) return;
    registeredUserIdRef.current = userId;
    registerForPushNotifications().catch((error) => {
      logger.warn('Push registration failed:', error);
    });
  }, [userId]);

  // Handle notification taps (cold start + while running).
  useEffect(() => {
    if (Platform.OS === 'web') return;

    const handleTap = (tap: NotificationTap | null) => {
      if (!tap?.link || handledTapIdsRef.current.has(tap.id)) return;
      handledTapIdsRef.current.add(tap.id);

      const { href, openInApp } = resolveAppLink(tap.link);
      if (openInApp) {
        router.push(href as never);
      } else {
        Linking.openURL(href).catch((error) => {
          logger.warn('Failed to open push link externally:', error);
        });
      }
    };

    const removeListener = addNotificationTapListener(handleTap);
    getInitialNotificationTap()
      .then(handleTap)
      .catch((error) => logger.warn('Initial notification check failed:', error));

    return removeListener;
  }, []);
}
